import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useClerk } from '@clerk/clerk-react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import queryClient from '../../queryClient';
import { resetRegimeCurrency } from 'stores/useRegimeCurrencyStore';
import useUploadHubStore from 'modules/UploadHub/stores/useUploadHubStore';
import { usePlanningScenarioStore } from 'modules/CbamCosts/stores/usePlanningScenarioStore';
import http from 'helpers/requests';
import CgPageLoader from 'modules/Common/layout/CgPageLoader';
import { overviewPathForOrgType } from './overviewPath';
import { consumeManualOrgNav } from './orgSwitchNav';
import { useOrgSwitchStore } from './orgSwitchStore';
import { ActiveOrgContext } from './activeOrgContext';
import {
  PermissionsProvider,
  usePermissionsContext,
} from 'modules/Common/permissions/PermissionsProvider';
import { useEnforceOrgLanguagePolicy } from 'modules/Common/config/languageAccess';

// Clerk derives `clerk.organization` by looking the session's
// `lastActiveOrganizationId` up in the user's loaded memberships:
//
//   session.user.organizationMemberships
//     .map(m => m.organization)
//     .find(o => o.id === session.lastActiveOrganizationId) || null
//
// so it resolves to NULL for as long as that id points at an org the membership
// list hasn't caught up with — precisely the window a switch opens. Treating
// that blip as "the user has no org" publishes a null active org, which the
// route guard reads as `no-org` and bounces to /init; InitPage then re-routes
// off the stale useOrganization() hook and lands on the previous org's
// overview, which the new org can't access → /not-found.
//
// `lastActiveOrganizationId` is the authoritative answer to "is an org active",
// and it is set before the membership list refreshes, so it distinguishes the
// blip from a genuine org loss (where Clerk clears the id too).
const hasActiveOrgId = (clerk) => Boolean(clerk?.session?.lastActiveOrganizationId);

// Switching organizations used to trigger a full `window.location.reload()`.
// The reason a reload was load-bearing: after `setActive({ organization })`,
// Clerk's `useOrganization()` hook does NOT reliably re-render with the new org
// on a same-session switch (a known reactivity gap), and `getToken()` keeps
// returning the previous org's cached token for ~60s. A reload was the brute-
// force way to force every consumer to re-read fresh state.
//
// OrgBoundary reproduces that "everything re-reads fresh" guarantee WITHOUT a
// reload, and crucially does NOT depend on the unreliable hook:
//   1. The active org id is read from Clerk's authoritative imperative channel
//      — `clerk.addListener` + the live `clerk.organization` resource — which
//      updates even when the React hook is stale. We mirror it into our own
//      useState so a change is guaranteed to re-render.
//   2. On a real switch we `refreshClerkToken()` (skipCache — see requests.js)
//      to swap the orgId-keyed backend JWT, then `queryClient.clear()` so every
//      query refetches under the new org with the new token.
//   3. `key={activeOrgId}` remounts the whole in-app tree. The remount is what
//      fixes the stale hook: every `useOrganization()` consumer is thrown away
//      and re-mounts reading the now-correct active org — exactly what the
//      reload achieved, minus the page reload.
//   4. The new org's `/permissions/me` must land BEFORE the tree is revealed.
//      OrgBoundary therefore owns <PermissionsProvider> and mounts it ABOVE the
//      loader branch (see the render at the bottom), so the fetch overlaps the
//      loader instead of starting on the frame the loader drops. It used to be
//      a child of this component in _app.jsx, i.e. unmounted for the whole
//      switch — so every switch revealed the app with permissions still
//      `loading`, which every consumer that isn't a route guard reads as DENY:
//      blank pages, a nav bar missing items, hidden actions, and (via
//      WorkspacePlanBoundary) a "no access" screen that isn't true.
export const OrgBoundary = ({ children }) => {
  const { t } = useTranslation('app');
  const clerk = useClerk();
  const history = useHistory();
  // The router's committed location. Used to gate the loader-drop on the
  // redirect having actually landed (see pendingDestRef below) — NOT read for
  // any rendering decision here.
  const location = useLocation();
  // Track the current org id imperatively (not via useOrganization() — see
  // comment above). Seed from the live resource so the initial mount is a no-op.
  const lastOrgIdRef = useRef(clerk?.organization?.id ?? null);
  // The path a real switch is redirecting to. While it is set, the switch loader
  // stays up until the router's committed `location` actually reaches it. This
  // closes the flash: `endSwitch()` is a zustand update, and zustand's
  // useSyncExternalStore forces a *synchronous* re-render that would otherwise
  // reveal the route tree BEFORE React commits BrowserRouter's location update
  // from history.replace (an ordinary batched setState). In that one frame the
  // children mounted at the OLD url with the NEW org, the old route's org-type
  // gate rejected the new org, and /not-found flashed until the location caught
  // up. Confirmed from a production trace: window.location was already
  // '/operator-overview' while the '/overview' declarant route was still
  // rendering against the operator org. Gating the loader on location===dest
  // makes the reveal causal instead of scheduler-dependent: children can only
  // mount once BOTH the org and the location are the new values.
  const pendingDestRef = useRef(null);
  // The authoritative active org, mirrored from Clerk's imperative resource and
  // published via ActiveOrgContext so the route org-type gate (useAuthReadiness
  // → PrivateRoute) never reads the lagging useOrganization() hook. Seeded from
  // the live resource so the initial mount already has the correct org.
  const [activeOrg, setActiveOrg] = useState(() => clerk?.organization ?? null);
  // Stable context value; `isLoaded` gates on Clerk being ready so guards defer
  // (status 'loading') instead of falsely reading 'no-org' before Clerk hydrates.
  const activeOrgValue = useMemo(
    () => ({ organization: activeOrg, isLoaded: !!clerk }),
    [activeOrg, clerk],
  );
  // A counter that increments ONLY on a real org->org switch. It drives the
  // remount key. We deliberately do NOT remount on null->org acquisition
  // (onboarding/first-login): that would wipe the in-progress state of the
  // onboarding components that call setActive while still mounted
  // (OperatorInfoForm's activation flags, OnboardingWizard's Calendly step).
  const [switchCount, setSwitchCount] = useState(0);
  // The org id <PermissionsProvider> may fetch for. Deliberately NOT the active
  // org id: it is published only once the new-org token has been minted and the
  // query cache cleared, because a /permissions/me that starts before either
  // would race the token or be wiped by queryClient.clear(). Seeded from the
  // live resource so the initial mount fetches immediately.
  const [permissionsOrgId, setPermissionsOrgId] = useState(() => clerk?.organization?.id ?? null);
  // Non-null while a real switch is still waiting on that org's permissions.
  // The gate below keeps the loader up until the provider reports BOTH this org
  // and a settled status, then this clears (one-shot — a later background
  // refetch must never re-raise a fullscreen loader over a working app).
  const [awaitingPermissionsOrgId, setAwaitingPermissionsOrgId] = useState(null);
  const handlePermissionsSettled = useCallback(() => setAwaitingPermissionsOrgId(null), []);
  // Full-screen switch loader state, shared with the switch handlers (NavSidebar)
  // via a store so they can flip it ON the instant the user clicks — before
  // setActive() resolves — for immediate feedback. We keep the loader up for the
  // WHOLE transition of every real switch (not just navigating ones): while it
  // shows, the route tree is unmounted, so no page guard renders against the
  // transient stale/cache-cleared org state (that window used to flash a
  // not-found / bounce to /init).
  const isSwitching = useOrgSwitchStore((s) => s.isSwitching);

  // Operators and consultants run in English until their surfaces are
  // translated. Enforced HERE because this is where the authoritative org type
  // lands first (the useOrganization() hook lags a switch), so the pin is
  // already in place by the time the remounted tree renders a page.
  useEnforceOrgLanguagePolicy(activeOrg?.publicMetadata?.type);

  useEffect(() => {
    if (!clerk) return undefined;

    const applyOrg = async (nextOrg) => {
      const nextId = nextOrg?.id ?? null;
      if (nextId === lastOrgIdRef.current) return;
      // Clerk emits transient organization:null snapshots whenever the session's
      // lastActiveOrganizationId is ahead of the loaded membership list — during
      // a switch, and on any session touch that refreshes resources afterwards.
      // Processing one would corrupt lastOrgIdRef (making the real emission look
      // like null->org onboarding, so the cache clear, redirect and remount are
      // all skipped), consume the handler's manual-nav claim, drop the in-flight
      // loader, and publish a null active org that the route guard reads as
      // `no-org`. Ignore it; the emission carrying the org follows.
      //
      // Gating on `isSwitching` alone was not enough: it is already false for a
      // blip that arrives after the switch settles, and it is never set at all
      // for a switch started by Clerk's own <OrganizationSwitcher>, which does
      // not go through performOrgSwitch.
      if (nextId == null && (hasActiveOrgId(clerk) || useOrgSwitchStore.getState().isSwitching))
        return;
      // Real switch = moving between two existing orgs. Gaining an org
      // (null -> org) is onboarding/first-login and is handled by that flow's
      // own navigation — we only refresh the token for it, nothing else.
      const isRealSwitch = lastOrgIdRef.current != null && nextId != null;
      // Read (and reset) the manual-nav claim synchronously, before any await —
      // and only for the real-switch emission it belongs to, so an unrelated
      // emission can't consume a handler's claim. The claim is the handler's
      // destination PATH (string), or `true` for a path-less claim, or null.
      const manualNav = isRealSwitch ? consumeManualOrgNav() : null;
      const handlerWillNavigate = Boolean(manualNav);
      // The handler's destination, when it told us. Lets us gate the loader on
      // the router reaching it — the same flash fix the sidebar switch uses.
      const handlerDest = typeof manualNav === 'string' ? manualNav : null;
      const nextType = nextOrg?.publicMetadata?.type ?? null;
      lastOrgIdRef.current = nextId;
      // Publish the authoritative org for route guards. Runs for real switches
      // AND onboarding acquisition (null->org); the transient-null guard and the
      // same-id early return above both bail before here, so we never publish a
      // stale/null blip mid-switch.
      setActiveOrg(nextOrg);
      // Cover the whole transition (token refresh + redirect + remount) with the
      // loader so the route tree stays unmounted and no in-between guard fires on
      // stale/cleared state. Idempotent with the handler's own beginSwitch(), and
      // catches switches that originate elsewhere (Clerk's OrganizationSwitcher,
      // consultant impersonation) so those get the loader too.
      // Call the store actions imperatively (getState) so they aren't effect
      // deps — they're stable but the linter can't prove it for a zustand action.
      // Arm the permissions gate in the same breath, BEFORE any await, so there
      // is no window in which the loader could drop on the OLD org's (still
      // `ready`) permissions.
      if (isRealSwitch) {
        useOrgSwitchStore.getState().beginSwitch();
        // `flushSync` because the next two statements are ordered against
        // `queryClient.clear()` below, not against React's scheduler.
        // <PermissionsProvider> is mounted for the whole switch now, so at the
        // moment we clear the cache it still has an ACTIVE observer on the
        // previous org's key — and clearing a cache out from under an active
        // observer makes react-query immediately refetch it. That resurrected
        // request goes out with the new org's token (getClerkToken re-mints on
        // an org mismatch) and lands the NEW org's payload under the OLD org's
        // key: the precise mis-scoping phase 2 existed to kill. Parking the
        // query id at null disables the observer, so there is nothing to
        // resurrect. Without flushSync the parking would land whenever React
        // next renders — after a real network round-trip in production, but
        // after the clear in tests — i.e. the correctness of the cache would
        // depend on how slow the token mint was.
        flushSync(() => {
          setAwaitingPermissionsOrgId(nextId);
          setPermissionsOrgId(null);
        });
      }
      // True once we hand the loader-drop off to the location watcher below, so
      // the finally does NOT drop it early (which is the whole flash).
      let loaderHandedOff = false;
      try {
        if (nextId != null) {
          try {
            // Mint a token carrying the new orgId BEFORE anything refetches, so
            // the remount's queries authenticate as the new org instead of
            // racing the stale cached token.
            await http.refreshClerkToken();
          } catch (err) {
            // A failed proactive mint must NOT abort the switch — the cache
            // clear, redirect and remount below still run (revealing the old
            // org's cached data under the new org would be worse), and the
            // per-request token path retries on demand.
            console.error('Failed to refresh backend token after org change:', err);
          }
        }
        if (isRealSwitch) {
          queryClient.clear();
          // The regime's currency is org-scoped, so it must not outlive the
          // org it describes: switching from a UK declarant to an EU one with
          // a stale 'GBP' in the store would paint `£` on euros until the new
          // profile lands. Cleared beside the cache for the same reason and at
          // the same moment; `getRegimeCurrency()` reads an empty store as
          // euros, and AppLayout's `useRegimeProfile` refills it for the new
          // org on the remount below.
          resetRegimeCurrency();
          // The Upload Hub's files and batch belong to the org they were
          // uploaded under, and its store outlives the remount: without this
          // the new org opens the previous org's hub session.
          useUploadHubStore.getState().reset();
          // A planning scenario and its parts (verification what-if, simulated
          // purchases, hedges) are the previous org's: without this the new org's
          // figures net them, and a save would write them into the new org.
          usePlanningScenarioStore.getState().exit();
        }
        // Release the permissions query onto the new org. Everything it depends
        // on is now in place: the token above carries the new orgId, and the
        // cache clear is done (so this fetch can neither be wiped by it nor
        // land under the old key). Runs for onboarding acquisition too — that
        // path arms no gate, so it raises no loader; it just stops the provider
        // from having to wait for useAuth().orgId to catch up.
        setPermissionsOrgId(nextId);
        if (isRealSwitch) {
          // Land on the new org's overview — directly, NOT via "/" (which
          // redirects to /init, whose onward redirect depends on the stale-
          // after-switch useOrganization()). Every real switch redirects: the
          // previous same-type-stays-put behavior kept users on entity-scoped
          // detail routes (/imports/<id>, /suppliers/<id>) whose resource
          // belongs to the OLD org — a guaranteed 404/guard-bounce under the
          // new org's token. Custom handlers that navigate themselves opt out
          // via markManualOrgNav().
          if (!handlerWillNavigate) {
            // Sidebar / Clerk-switcher path: OrgBoundary owns the redirect.
            // An org with no recognisable type has no overview we can send it
            // to — every one of them is type-gated. /init resolves the
            // destination from the authoritative org (and routes an
            // un-onboarded org into onboarding), so it is the only safe
            // fallback; guessing an overview here is a guaranteed guard bounce.
            const dest = overviewPathForOrgType(nextType) ?? '/init';
            // Hand the loader-drop to the location watcher: keep it up until the
            // router has committed `dest`, so children only ever mount once the
            // location matches the new org's route (see pendingDestRef). If the
            // router is somehow already there, the watcher still fires because
            // it also keys on switchCount (which changes just below).
            pendingDestRef.current = dest;
            loaderHandedOff = true;
            history.replace(dest);
          } else if (handlerDest) {
            // Handler-navigated switch ("Back to Consultant", consultant open-
            // client) that told us its destination. The handler still performs
            // the actual navigation (it also runs side effects like
            // clearConsultantOrgId); we DON'T redirect. But we gate the loader
            // on that same destination so the handler's old route can't render
            // against the new org for a frame — the exact flash the sidebar
            // switch had. Same causal gate, just a path we were handed rather
            // than one we computed.
            pendingDestRef.current = handlerDest;
            loaderHandedOff = true;
          } else {
            // Handler navigates but didn't tell us where (path-less claim). We
            // can't gate on a path, so fall through to the finally, which drops
            // the loader once the switch work has settled (legacy behavior).
          }
          // Remount the tree so every useOrganization() consumer re-reads the new
          // org. Only on a real switch — never mid-onboarding (see switchCount).
          setSwitchCount((count) => count + 1);
        }
      } finally {
        // Belt and braces for the permissions gate: if anything above threw
        // between parking the query id at null and releasing it, the gate would
        // sit on a disabled (= permanently `loading`) query until the 15s
        // watchdog fired. Re-setting the same value in the happy path is a
        // no-op — React bails out of an identical state update.
        if (isRealSwitch) setPermissionsOrgId(nextId);
        // Drop the loader now for everything EXCEPT the case we handed to the
        // location watcher — a null->org onboarding change, a handler-navigated
        // switch, or a failure above. For the OrgBoundary-navigated real switch
        // the watcher clears it once `location` reaches the destination, so a
        // switch can never leave the app stuck behind the loader either way (a
        // watchdog also force-clears after 15s).
        if (!loaderHandedOff) useOrgSwitchStore.getState().endSwitch();
      }
    };

    // Catch a switch that landed between initial render and effect attachment.
    applyOrg(clerk.organization ?? null);

    // addListener fires on every resource change (token refresh, user update,
    // org switch …); we act only when the org id actually changes.
    return clerk.addListener((resources) => {
      applyOrg(resources.organization ?? null);
    });
  }, [clerk, history]);

  // Drop the switch loader once the router has actually committed the
  // destination of an OrgBoundary-navigated real switch. Keying on both
  // `location.pathname` and `switchCount` covers both orderings: the redirect
  // landing on a later commit (location changes → effect runs → match), and
  // the router somehow already being at `dest` (switchCount changes → effect
  // runs → match). Until it matches, the loader stays up and the route tree
  // stays unmounted, so no guard can render the old route against the new org.
  useEffect(() => {
    if (pendingDestRef.current && location.pathname === pendingDestRef.current) {
      pendingDestRef.current = null;
      useOrgSwitchStore.getState().endSwitch();
    }
  }, [location.pathname, switchCount]);

  // Watchdog: the loader replaces the ENTIRE route tree, so a beginSwitch()
  // that is never paired with an endSwitch() (a handler bug, setActive
  // hanging, an emission that never arrives, or a redirect whose destination
  // the router never reaches) would brick the app behind an undismissable
  // fullscreen loader. If a switch hasn't settled after 15s, force-clear it —
  // worst case the user sees the old page and can retry.
  useEffect(() => {
    if (!isSwitching) return undefined;
    const timer = setTimeout(() => {
      console.error('Organization switch did not settle within 15s — clearing the switch loader.');
      useOrgSwitchStore.getState().endSwitch();
    }, 15000);
    return () => clearTimeout(timer);
  }, [isSwitching]);

  // Same watchdog for the permissions half of the gate. /permissions/me settles
  // to `ready` or `error` in practice, but `status` also reports `loading`
  // whenever the query is disabled (Clerk momentarily reporting signed-out, or
  // an org id that never arrives) — and that would hold the fullscreen loader
  // forever. Time-box it and let the route guards deal with the fallout.
  useEffect(() => {
    if (!awaitingPermissionsOrgId) return undefined;
    const timer = setTimeout(() => {
      console.error('Permissions did not resolve within 15s — revealing the app anyway.');
      setAwaitingPermissionsOrgId(null);
    }, 15000);
    return () => clearTimeout(timer);
  }, [awaitingPermissionsOrgId]);

  return (
    <ActiveOrgContext.Provider value={activeOrgValue}>
      {/* Above the loader on purpose — see point 4 of the header comment. The
          provider stays mounted for the whole switch so its fetch overlaps the
          loader; only `children` are torn down and re-keyed. */}
      <PermissionsProvider orgId={permissionsOrgId}>
        <OrgSwitchGate
          isSwitching={isSwitching}
          awaitingOrgId={awaitingPermissionsOrgId}
          onSettled={handlePermissionsSettled}
          ariaLabel={t('app:orgBoundary.switchingOrganization', 'Switching organization')}
        >
          <React.Fragment key={switchCount}>{children}</React.Fragment>
        </OrgSwitchGate>
      </PermissionsProvider>
    </ActiveOrgContext.Provider>
  );
};

// Holds the fullscreen switch loader up until BOTH halves of a switch have
// landed: the org/route transition (`isSwitching`, owned by OrgBoundary above)
// and the new org's /permissions/me.
//
// Lives below <PermissionsProvider> because it has to consume the context that
// provider creates.
const OrgSwitchGate = ({ isSwitching, awaitingOrgId, onSettled, ariaLabel, children }) => {
  const { orgId: permissionsOrgId, status } = usePermissionsContext();
  // Judged against the TARGET org, not `status` alone. Between arming the gate
  // (before the token refresh) and re-keying the query (after the cache clear)
  // the provider still reports the PREVIOUS org's `ready` — a status-only check
  // would read that as done and reveal the app on the old org's permissions.
  //
  // `!== 'loading'` rather than `=== 'ready'`: a failed /permissions/me must
  // drop the loader so PermissionRoute/PrivateRoute can render
  // PermissionsErrorState, which has a retry. A fullscreen loader on an error
  // is a brick.
  const settled =
    awaitingOrgId != null && permissionsOrgId === awaitingOrgId && status !== 'loading';

  useEffect(() => {
    if (settled) onSettled();
  }, [settled, onSettled]);

  if (isSwitching || (awaitingOrgId != null && !settled)) {
    return (
      <CgPageLoader
        variant="fullscreen"
        ariaLabel={ariaLabel}
      />
    );
  }

  return children;
};

export default OrgBoundary;
